import type Parse from 'parse';

import { CaseType } from '@telespot/domain';

import { ParseMapper } from '../../parse.mapper';
import { ObjectTopology, SoftDeletableTopology } from '../../parse.topology';

import { ParseCaseDataFieldMapper, PersistedCaseDataField } from './parse-case-data-field.mapper';
import { MethodTypeTopology } from './parse-method-type.mapper';

export enum CaseTypeTopology {
  TABLE = 'CaseType',
  DESCRIPTION = 'description',
  NAME = 'name',
  FIELDS = 'fields',
  METHOD_TYPES = 'methodTypes',
}

export class ParseCaseTypeMapper extends ParseMapper {
  public toDomain(parseObject: Parse.Object) {
    return new CaseType({
      id: parseObject.id,
      name: parseObject.get(CaseTypeTopology.NAME),
      description: parseObject.get(CaseTypeTopology.DESCRIPTION) ?? '',
      fields: (parseObject.get(CaseTypeTopology.FIELDS) ?? []).map((field: PersistedCaseDataField) =>
        ParseCaseDataFieldMapper.toDomain(field)
      ),
      methodTypeIds: parseObject.get(CaseTypeTopology.METHOD_TYPES)?.map((mt) => mt.id),
      createdAt: parseObject.get(ObjectTopology.CREATED_AT),
      updatedAt: parseObject.get(ObjectTopology.UPDATED_AT),
      deletedAt: parseObject.get(SoftDeletableTopology.DELETED_AT),
    });
  }

  public fromDomain(caseType: CaseType) {
    const ParseCaseType = this.subclasses.getSubclass(CaseTypeTopology.TABLE);

    const parseMethodTypes = caseType.methodTypeIds.map((item) =>
      this.subclasses.getSubclass(MethodTypeTopology.TABLE).createWithoutData(item)
    );

    // A case type without an id is being created: instantiating the bare subclass
    // lets the server assign the object id on save (`fromJSON` would demand one).
    const parseObject = caseType.id
      ? this.parse.Object.fromJSON({
          className: CaseTypeTopology.TABLE,
          [ObjectTopology.ID]: caseType.id,
          [ObjectTopology.CREATED_AT]: caseType.createdAt,
          [ObjectTopology.UPDATED_AT]: caseType.updatedAt,
        })
      : new ParseCaseType();

    parseObject.set({
      [SoftDeletableTopology.DELETED_AT]: caseType.deletedAt,
      [CaseTypeTopology.NAME]: caseType.name,
      [CaseTypeTopology.DESCRIPTION]: caseType.description,
      [CaseTypeTopology.FIELDS]: caseType.fields.map((field) => ParseCaseDataFieldMapper.fromDomain(field)),
      [CaseTypeTopology.METHOD_TYPES]: parseMethodTypes,
    });

    return parseObject;
  }
}
